import type {
  CompositionV2Filters,
  CompositionV3Corrections,
  CompositionV3Filters,
} from '@revelio/data-access';

import { getNumericIdFromNestedId } from '../get-numeric-id-from-nested-id';
import {
  appendRoleSelectionToFilters,
  getSkillFilterKeyForTier,
  ROLE_LEVEL_ID_TO_FILTER_KEY,
  type RoleTaxonomyLevelId,
} from '../role-skill-ent-kind';

type LegacyCompositionFilterInput = Partial<CompositionV2Filters> &
  Partial<Record<RoleTaxonomyLevelId, number[] | null | undefined>> & {
    snapshot_date?: string;
  };

/**
 * Maps the non-date selections of a legacy composition filter object to
 * `CompositionV3Filters`. Drops deprecated `industry`; maps `custom_role` /
 * `custom_skill` into `role_level_*` / `skills_level_*`; maps `role_k*` to
 * `role_level_*`. Dates are left to the caller (snapshot vs. range).
 */
export const mapCompositionV2FilterSelectionsToV3 = (
  filters: LegacyCompositionFilterInput
): CompositionV3Filters => {
  const {
    raw: _raw,
    custom_role,
    custom_skill,
    industry: _industry,
    contingent_workers: _contingent_workers,
    snapshot_date: _snapshot_date,
    start_date: _start_date,
    end_date: _end_date,
    ...rest
  } = filters;

  const restSansLegacyRoleKeys: Record<string, unknown> = { ...rest };
  (Object.keys(ROLE_LEVEL_ID_TO_FILTER_KEY) as RoleTaxonomyLevelId[]).forEach(
    (levelId) => {
      delete restSansLegacyRoleKeys[levelId];
    }
  );

  const out: CompositionV3Filters = {
    ...(restSansLegacyRoleKeys as CompositionV3Filters),
  };

  (Object.keys(ROLE_LEVEL_ID_TO_FILTER_KEY) as RoleTaxonomyLevelId[]).forEach(
    (levelId) => {
      const ids = filters[levelId];
      if (!ids?.length) {
        return;
      }
      const filterKey = ROLE_LEVEL_ID_TO_FILTER_KEY[levelId];
      out[filterKey] = [...(out[filterKey] ?? []), ...ids];
    }
  );

  let merged: CompositionV3Filters = out;
  for (const ent of custom_role?.entities ?? []) {
    const id = getNumericIdFromNestedId(String(ent?.id ?? ''));
    const levelId =
      ent?.levelId !== undefined && ent?.levelId !== null
        ? String(ent.levelId)
        : '';
    if (id === null || levelId === '') {
      continue;
    }
    merged = {
      ...merged,
      ...appendRoleSelectionToFilters(merged, levelId, id),
    };
  }
  for (const ent of custom_skill?.entities ?? []) {
    const entityId = ent?.entity_id;
    const levelIdNum = ent?.level_id;
    if (
      entityId === undefined ||
      entityId === null ||
      levelIdNum === undefined ||
      levelIdNum === null
    ) {
      continue;
    }
    const filterKey = getSkillFilterKeyForTier(levelIdNum);
    if (filterKey === null) {
      continue;
    }
    const prevRaw = merged[filterKey];
    const prev = Array.isArray(prevRaw) ? prevRaw : [];
    merged = {
      ...merged,
      [filterKey]: [...prev, entityId],
    };
  }

  return merged;
};

/**
 * Maps legacy composition tooltip / snapshot-style filter objects to
 * `CompositionV3Filters`, pinning the time range to a single snapshot month.
 */
export const mapTooltipFiltersToCompositionV3Filters = ({
  filters,
  snapshotDate,
}: {
  filters: LegacyCompositionFilterInput;
  snapshotDate: string;
}): CompositionV3Filters => ({
  ...mapCompositionV2FilterSelectionsToV3(filters),
  start_date: snapshotDate,
  end_date: snapshotDate,
});

export const mapTooltipFiltersToCompositionV3Corrections = ({
  filters,
}: {
  filters: Partial<CompositionV2Filters>;
}): CompositionV3Corrections => {
  const { raw, contingent_workers } = filters;
  return {
    with_sample_weight: true,
    with_weight: raw !== true,
    with_contingent_workers: contingent_workers ?? false,
  };
};
